import { useEffect, useRef } from 'react'
import { Bar, BarChart, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { formatAbbreviatedValue } from '@utils/functions/formatAbbreviatedValue'

import { CustomTooltip } from '../CustomTooltip'
import { getAccountAxis } from '../../helpers/getAccountAxis'

import type { AccountBarChartProps, AccountBarKey } from './types'

import * as S from './styles'

const BAR_KEYS: { key: AccountBarKey; label: string }[] = [
  { key: 'budget', label: 'Budget' },
  { key: 'week1', label: 'Semana 1' },
  { key: 'week2', label: 'Semana 2' },
  { key: 'week3', label: 'Semana 3' },
  { key: 'week4', label: 'Semana 4' },
  { key: 'week5', label: 'Semana 5' }
]

/** RF02: altura mínima visual de 2% do topo do eixo, para barra zerada continuar visível e clicável */
const MIN_BAR_RATIO = 0.02

/**
 * O tooltip é ancorado no topo do painel (`position={{ y: 0 }}`) e cresce para baixo, podendo chegar
 * a ~330px com as 6 barras (Budget + 5 semanas). A altura acomoda o tooltip inteiro dentro do painel.
 */
const CHART_HEIGHT = 430

/** Largura aproximada de um caractere do rótulo do eixo (12px, fonte do tema) */
const TICK_CHAR_WIDTH = 9
/** Espaço entre o fim do texto e a área do gráfico (tickSize + tickMargin do recharts) */
const Y_AXIS_PADDING = 14
const MIN_Y_AXIS_WIDTH = 40

/**
 * RC01: um gráfico por conta, com eixo Y exclusivo (RC02) e tooltip próprio (RC06).
 */
export const AccountBarChart = ({ account }: AccountBarChartProps) => {
  const chartWrapperRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const adjustTooltipPosition = () => {
      const tooltipWrapper = chartWrapperRef.current?.querySelector('.recharts-tooltip-wrapper') as HTMLElement
      if (!tooltipWrapper || tooltipWrapper.style.display === 'none') return

      const tooltipRect = tooltipWrapper.getBoundingClientRect()
      const windowWidth = window.innerWidth
      const tooltipRight = tooltipRect.right
      const tooltipLeft = tooltipRect.left
      const tooltipWidth = tooltipRect.width

      if (tooltipRight > windowWidth - 10) {
        const currentLeft = parseFloat(tooltipWrapper.style.left) || 0
        const newLeft = currentLeft - tooltipWidth - 20
        tooltipWrapper.style.left = `${newLeft}px`
      } else {
        const currentLeft = parseFloat(tooltipWrapper.style.left) || 0
        if (tooltipLeft < 0) {
          const originalX = currentLeft + tooltipWidth + 20
          tooltipWrapper.style.left = `${originalX}px`
        }
      }
    }

    const observer = new MutationObserver(() => {
      setTimeout(adjustTooltipPosition, 0)
    })

    if (chartWrapperRef.current) {
      observer.observe(chartWrapperRef.current, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class']
      })
    }

    window.addEventListener('resize', adjustTooltipPosition)
    window.addEventListener('mousemove', adjustTooltipPosition)

    return () => {
      observer.disconnect()
      window.removeEventListener('resize', adjustTooltipPosition)
      window.removeEventListener('mousemove', adjustTooltipPosition)
    }
  }, [])

  /** RN02 / RC08: a barra sempre é desenhada positiva — o sinal original fica em `account` para o tooltip */
  const axis = getAccountAxis(
    BAR_KEYS.map(({ key }) => account[key].value),
    account.formatAsPercent
  )
  const minBarValue = axis.max * MIN_BAR_RATIO

  const chartData = [
    BAR_KEYS.reduce(
      (row, { key }) => {
        const absoluteValue = Math.abs(account[key].value)
        row[key] = absoluteValue === 0 ? minBarValue : Math.max(absoluteValue, minBarValue)
        return row
      },
      { name: account.name } as Record<string, string | number>
    )
  ]

  /** RN03 / RN04: GM em percentual, todas as demais contas em moeda abreviada */
  const formatTick = (value?: number) => {
    if (typeof value !== 'number') return ''
    return account.formatAsPercent ? `${Math.round(value)}%` : formatAbbreviatedValue(value)
  }

  /**
   * O eixo precisa ser largo o bastante para o maior rótulo da própria conta ("500M" é mais largo
   * que "1.5B", e ambos são mais largos que "2K"). Com largura fixa, o texto excedente é desenhado
   * à esquerda do painel e invade o painel vizinho (ou sai da linha, no primeiro).
   */
  const longestTickLength = Math.max(...axis.ticks.map((tick) => formatTick(tick).length))
  const yAxisWidth = Math.max(MIN_Y_AXIS_WIDTH, longestTickLength * TICK_CHAR_WIDTH + Y_AXIS_PADDING)

  return (
    <S.AccountChartWrapper ref={chartWrapperRef}>
      <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
        <BarChart data={chartData} margin={{ top: 16, left: 0, right: 8, bottom: 0 }} barCategoryGap="8%" barGap={2}>
          <XAxis dataKey="name" tick={false} axisLine={false} height={8} />
          <YAxis
            width={yAxisWidth}
            tickFormatter={formatTick}
            ticks={axis.ticks}
            domain={[0, axis.max]}
            axisLine={false}
            tickLine={false}
            interval={0}
          />
          <Tooltip
            content={<CustomTooltip data={[account]} />}
            shared={true}
            cursor={{ fill: 'transparent' }}
            allowEscapeViewBox={{ x: true, y: true }}
            position={{ y: 0 }}
          />

          {BAR_KEYS.map(({ key, label }) => (
            <Bar key={key} dataKey={key} name={label} radius={[4, 4, 0, 0]}>
              <Cell key={`cell-${key}`} fill={account[key].color} />
            </Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>
    </S.AccountChartWrapper>
  )
}
